import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../store/languageStore';
import authService from '../shared/services/authService';
import { NAV_ITEMS } from '../layouts/AppShell';
import { useCardVisibilityStore } from '../store/cardVisibilityStore';
import ForgotPasswordModal from './ForgotPasswordModal';

// Schema will be created inside component to use translations

interface FormData {
  username: string;
  password: string;
}

const LoginModal: React.FC = () => {
  const { showLoginModal, setShowLoginModal, setShowRegisterModal } = useAppStore();
  const { setAuth } = useAuthStore();
  const { t, tString } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string>('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  const schema = yup.object({
    username: yup.string()
      .required(tString('auth.usernameRequired'))
      .min(3, tString('auth.usernameMinLength'))
      .max(255, tString('auth.usernameMaxLength')),
    password: yup.string()
      .required(tString('auth.passwordRequired'))
      .min(1, tString('auth.passwordEmpty'))
      .max(100, tString('auth.passwordMaxLength')),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const handleClose = () => {
    setShowLoginModal(false);
    setLoginError('');
    setShowForgotPassword(false);
    reset();
  };

  const clearErrorOnChange = () => {
    if (loginError) {
      setLoginError('');
    }
  };

  const onSubmit = async (data: FormData, event?: React.BaseSyntheticEvent) => {
    // Prevent default form submission behavior
    if (event) {
      event.preventDefault();
    }
    
    // Clear any existing errors
    setLoginError('');
    
    // Additional client-side validation
    if (!data.username?.trim()) {
      setLoginError(`❌ ${tString('auth.usernameRequired')}`);
      return;
    }

    if (!data.password?.trim()) {
      setLoginError(`❌ ${tString('auth.passwordRequired')}`);
      return;
    }
    
    try {
      setIsLoading(true);
      
      const response = await authService.login(data);
      
      setAuth(response.user, response.token);
      toast.success(response.message || tString('auth.loginSuccessful'));
      handleClose();

      // Resolve the post-login destination. Pinned route wins if it's still
      // visible to the user (card not admin-disabled, not disabled/coming-soon).
      // Stale pin → clear server-side + toast, then fall back to default.
      const defaultPath = response.user.is_admin ? '/admin/dashboard' : '/process';
      const pinned = response.user.pinned_nav_path || null;
      if (!pinned) {
        navigate(defaultPath);
      } else {
        const navItem = NAV_ITEMS.find(i => i.path === pinned);
        const isPinReachable =
          !!navItem && !navItem.disabled && navItem.path !== '#' &&
          (!navItem.cardKey || (useCardVisibilityStore.getState().cards[navItem.cardKey] ?? false));
        if (isPinReachable) {
          navigate(pinned);
        } else {
          toast.info('Your pinned module is no longer available — pin cleared.');
          // Best-effort cleanup. Failure here is non-fatal; the next login
          // will just see the same stale pin and re-run this branch.
          authService.setPinnedNavPath(null).catch(() => { /* ignore */ });
          navigate(defaultPath);
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);

      let errorMessage = `❌ ${tString('auth.loginFailedCredentials')}`;

      // Handle different types of errors with specific messages
      if (error.response) {
        // Server responded with an error status
        const status = error.response.status;
        const detail = error.response.data?.detail;

        switch (status) {
          case 401:
            if (detail && detail.toLowerCase().includes('incorrect')) {
              errorMessage = `❌ ${tString('auth.credentialsNoMatch')}`;
            } else {
              errorMessage = `❌ ${tString('auth.invalidUsernamePassword')}`;
            }
            break;
          case 400:
            if (detail && detail.toLowerCase().includes('inactive')) {
              errorMessage = `🚫 ${tString('auth.accountInactive')}`;
            } else if (detail && detail.toLowerCase().includes('password')) {
              errorMessage = `❌ ${tString('auth.invalidPasswordFormat')}`;
            } else if (detail && detail.toLowerCase().includes('username')) {
              errorMessage = `❌ ${tString('auth.invalidUsernameFormat')}`;
            } else {
              errorMessage = `❌ ${tString('auth.invalidLoginInfo')}`;
            }
            break;
          case 403:
            errorMessage = `🚫 ${tString('auth.accessDenied')}`;
            break;
          case 404:
            errorMessage = `⚠️ ${tString('auth.serviceUnavailable')}`;
            break;
          case 422:
            errorMessage = `❌ ${tString('auth.invalidFormat')}`;
            break;
          case 429:
            errorMessage = `⏳ ${tString('auth.tooManyAttempts')}`;
            break;
          case 500:
            errorMessage = `🔧 ${tString('auth.serverError')}`;
            break;
          case 502:
          case 503:
          case 504:
            errorMessage = `⚠️ ${tString('auth.serviceUnavailable')}`;
            break;
          default:
            if (detail) {
              errorMessage = detail;
            } else {
              errorMessage = `Login failed (Error ${status}). Please try again.`;
            }
        }
      } else if (error.request) {
        // Network error - no response received
        errorMessage = `🌐 ${tString('auth.networkError')}`;
      } else if (error.message) {
        // Other errors
        if (error.message.includes('Network Error')) {
          errorMessage = `🌐 ${tString('auth.connectionError')}`;
        } else if (error.message.includes('timeout')) {
          errorMessage = `⏱️ ${tString('auth.timeout')}`;
        } else {
          errorMessage = `❌ Login error: ${error.message}`;
        }
      }

      setLoginError(errorMessage);
      // Ensure modal stays open to display error message
      // Don't call handleClose() on error - keep modal open for user to retry
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    // Explicitly prevent any form submission that bypasses react-hook-form
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  const switchToRegister = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
    reset();
  };

  return (
    <>
      <Dialog
        open={showLoginModal}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: 400,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="span">
            {tString('auth.signIn')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

      <form onSubmit={handleFormSubmit} noValidate>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3}>
            <Typography variant="body2" color="textSecondary" textAlign="center">
              {tString('auth.loginSubtitle')}
            </Typography>

            {loginError && (
              <Alert 
                severity="error" 
                variant="filled"
                sx={{ 
                  mt: 1,
                  mb: 1,
                  '& .MuiAlert-message': {
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }
                }}
                onClose={() => setLoginError('')}
              >
                {loginError}
              </Alert>
            )}

            <Controller
              name="username"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    clearErrorOnChange();
                  }}
                  label={tString('auth.usernameOrEmail')}
                  variant="outlined"
                  fullWidth
                  error={!!errors.username}
                  helperText={errors.username?.message}
                  disabled={isLoading}
                  autoComplete="username"
                  placeholder={tString('auth.enterUsername')}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    clearErrorOnChange();
                  }}
                  label={tString('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  disabled={isLoading}
                  autoComplete="current-password"
                  placeholder={tString('auth.enterPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Box display="flex" justifyContent="center">
              <Button
                variant="text"
                onClick={() => setShowForgotPassword(true)}
                disabled={isLoading}
                sx={{ textTransform: 'none', fontSize: '0.875rem' }}
              >
                {tString('auth.forgotPassword')}
              </Button>
            </Box>

            <Box textAlign="center">
              <Typography variant="body2" color="textSecondary">
                {tString('auth.dontHaveAccount')}{' '}
                <Button
                  variant="text"
                  onClick={switchToRegister}
                  disabled={isLoading}
                  sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                >
                  {tString('auth.signUp')}
                </Button>
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            {tString('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{ minWidth: 100 }}
          >
            {isLoading ? tString('auth.signingIn') : tString('auth.signIn')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>

    <ForgotPasswordModal
      open={showForgotPassword}
      onClose={() => setShowForgotPassword(false)}
    />
  </>
  );
};

export default LoginModal;